/**
 * Reiner Kern der KI-Formulierhilfe im Mail-Composer (Task 57).
 *
 * Bewusst OHNE Laufzeit-Importe (kein DOM, kein React) — wie ``mailRichText.ts``:
 * so ist die Erkennung in reinem Node prüfbar und der Walk kann sie über den
 * dev-Test-Hook mit den ECHTEN Funktionen befragen. Die DOM-Arbeit (Selektion,
 * ``execCommand``, Einfügen) liegt in ``Mail.tsx``, wo der Editor lebt.
 *
 * Vorbild: das Word-Add-in „bitop Write" (``doc-ai/addin/src/word.ts:61-101``).
 */

export interface TriggerHit {
  /** Der Auslöser hat gegriffen. */
  fired: boolean;
  /**
   * Text aus ``<…>``. Gesetzt = **geführter Absatz**, ``null`` = reine
   * **Fortsetzung**. Das ist dieselbe Weiche, die das Backend nimmt: die
   * ANWESENHEIT der Anweisung schaltet den Modus um
   * (``app/schemas/mail.py`` ``ReplySuggestRequest.instruction``).
   */
  instruction: string | null;
  /**
   * Die Zeichen am ENDE des Absatzes, die beim Erfolg durch den erzeugten Text
   * ersetzt werden — im geführten Modus ``<Anweisung>>>``, sonst nur ``>>``.
   * Ihre LÄNGE ist die Zahl der Zeichen, die der Editor rückwärts selektiert.
   */
  trigger: string;
}

const NO_HIT: TriggerHit = { fired: false, instruction: null, trigger: "" };

/**
 * Was in spitzen Klammern KEINE Anweisung ist, sondern eine E-Mail-Adresse.
 *
 * ``<adresse@firma.de>`` ist in Mail die Standard-Schreibweise für einen
 * Empfänger — „Bitte um Rückmeldung an <meier@kunde.de>>>" ist eine ganz normale
 * Zeile. Ohne diese Ausnahme feuerte sie GEFÜHRT mit ``instruction =
 * "meier@kunde.de"``: das Modell bekäme eine Adresse als Auftrag und schriebe
 * einen Absatz aus dem Nichts, statt den Satz fortzusetzen.
 *
 * Bewusst eng: keine Leerzeichen, genau ein ``@``, ein Punkt danach — dieselbe
 * Form wie ``normalizeUrl`` in ``mailRichText.ts`` sie für ``mailto:`` nimmt.
 * ``<schreib an meier@kunde.de>>>`` enthält Leerzeichen und bleibt damit eine
 * Anweisung.
 */
const IST_ADRESSE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Auslöser-Erkennung auf dem Absatz, in dem die Schreibmarke steht.
 *
 *     Text den ich angefangen habe>>     → Fortsetzung
 *     <sag den Termin ab>>>              → geführter Absatz
 *
 * **Zitierte Historie feuert nicht.** In Mail ist ein führendes ``>`` am
 * Zeilenanfang die klassische Zitat-Markierung. Der Basis-Ausdruck fängt das
 * meiste schon ab (eine zitierte Zeile ENDET auf Text, nicht auf ``>>``), aber
 * eine leere Doppelzeile ``>>`` täte es sehr wohl — deshalb die zweite Regel:
 * beginnt der Absatz mit ``>``, ist er Historie und kein Auftrag.
 *
 * Der Preis dafür ist bewusst: ein blankes ``>>`` auf einer eigenen leeren
 * Zeile löst nicht aus. Das ist kein Verlust — die Fortsetzung setzt fort, was
 * dasteht, und wird deshalb ans Ende des eigenen Satzes getippt. Für einen
 * Absatz aus dem Nichts gibt es den geführten Modus.
 *
 * Gegen HTML-Zitate (Outlook zitiert OHNE ``>``-Präfixe) hilft dieser Ausdruck
 * NICHT — dort greift die DOM-Grenze ``findQuoteBoundary`` in ``Mail.tsx``.
 */
export function detectTrigger(paragraph: string): TriggerHit {
  const text = paragraph ?? "";
  const end = /(>>\s*)$/.exec(text);
  if (!end) return NO_HIT;
  if (/^\s*>/.test(text)) return NO_HIT;
  const before = text.slice(0, end.index);
  const brief = /<([^<>]+)>\s*$/.exec(before);
  if (brief) {
    const instruction = brief[1].trim();
    // Eine Adresse in spitzen Klammern ist Mail-Schreibweise, kein Auftrag →
    // durchfallen lassen; unten greift die Fortsetzung. Der Auslöser sind dann
    // die ZWEI ``>``, die der Nutzer selbst getippt hat — das schließende ``>``
    // der Adresse steht davor und bleibt stehen. Der Zwischenstand
    // ``<meier@kunde.de>>`` feuert nicht: dort ist die Klammer noch offen, und
    // die Regel für halbfertige Anweisungen weiter unten fängt ihn ab.
    if (instruction && !IST_ADRESSE.test(instruction)) {
      return { fired: true, instruction, trigger: text.slice(brief.index) };
    }
  }
  // Halbfertige Anweisung → NICHT als Fortsetzung feuern.
  //
  // Der Auslöser ist das Tippen selbst, und wer ``<sag den Termin ab>>>``
  // tippt, durchläuft zwangsläufig den Zwischenstand ``<sag den Termin ab>>``.
  // Der endet auf ``>>`` und wäre ohne diese Regel eine Fortsetzung — ausgelöst
  // mitten in der Anweisung, mit dem halben Auftrag als Entwurfstext. Das
  // Word-Vorbild kennt das Problem nicht, weil es per Knopf aufgerufen wird und
  // die Zwischenstände nie sieht.
  //
  // Preis dieser Regel: ein offenes ``<`` in derselben Zeile (``Preis < 100>>``)
  // unterdrückt die Fortsetzung. Das ist die richtige Richtung — lieber nichts
  // tun als den halben Auftrag ausführen.
  if (/<[^<>]*$/.test(before)) return NO_HIT;
  return { fired: true, instruction: null, trigger: end[1] };
}

/**
 * Einfache Markdown-Marker aus dem Modell-Text entfernen.
 *
 * Eingefügt wird über ``execCommand("insertText")`` — reiner Text, damit EIN
 * Strg+Z die Anweisung zurückholt. Reiner Text kann kein Fett; ein
 * durchgerutschtes ``**wichtig**`` stünde also LITERAL im Entwurf. Der Prompt
 * verbietet Markdown zwar ("ausschließlich der Fließtext", ``reply.py``), aber
 * ein Prompt ist keine Garantie — und literale Sternchen in einer Kundenmail
 * sind der teurere Fehler.
 */
export function stripInlineMarkers(s: string): string {
  return (s || "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "$1")
    .replace(/`([^`\n]+)`/g, "$1");
}

// ── Wiederholungs-Bremse ────────────────────────────────────────────────────
//
// Das Modell wiederholt gern das Ende des schon geschriebenen Textes, bevor es
// weiterschreibt. Die Prompt-Anweisung „WIEDERHOLE NICHT, was bereits dasteht"
// steht in ``app/mail/reply.py`` — und reicht nachweislich nicht: das Vorbild
// hat es gemessen (``doc-ai/addin/src/word.ts:120-131``, Auswertung
// ``scripts/eval_repeat.py``) und kommt auf rund ein Drittel Durchrutscher
// TROTZ ausdrücklicher Beispiele im Prompt.
//
// Deshalb sitzt die Bremse hier, im Frontend, und nicht im Prompt: nur hier ist
// bekannt, was WIRKLICH im Editor steht.

const MIN_UEBERLAPPUNG = 12;
const FENSTER = 200;

/** Normalisierte Fassung + Rückweg-Karte (Index je normalisiertem Zeichen). */
function normalisiere(s: string): { text: string; karte: number[] } {
  let text = "";
  const karte: number[] = [];
  let letzteWarLeer = false;
  for (let i = 0; i < s.length; i++) {
    const roh = s[i];
    if (/[\s ​]/.test(roh)) {
      if (letzteWarLeer) continue;
      letzteWarLeer = true;
      text += " ";
      karte.push(i);
      continue;
    }
    letzteWarLeer = false;
    let c = roh;
    // Typografie, die zwischen Editor und Modell gern abweicht.
    if ("‘’".includes(c)) c = "'";
    else if ("“”„".includes(c)) c = '"';
    else if ("–—".includes(c)) c = "-";
    const klein = c.toLowerCase();
    // Ein Zeichen rein, ein Zeichen raus — sonst zerreißt die Karte.
    text += klein.length === 1 ? klein : c;
    karte.push(i);
  }
  karte.push(s.length);
  return { text, karte };
}

export interface BremsErgebnis {
  text: string;
  /** Wie viele Zeichen die Bremse weggenommen hat — 0 = sie hat nicht gegriffen.
   *  Wird in der Statuszeile gezeigt, damit sichtbar ist, DASS sie greift. */
  entfernt: number;
}

/**
 * Beginnt ``erzeugt`` mit einer Wiederholung des Endes von ``davor``, wird
 * genau diese Überlappung abgeschnitten.
 *
 * Verglichen wird normalisiert (Groß/Klein, Anführungszeichen, Gedankenstriche,
 * Leerraum), zurückgeschnitten aber am ORIGINAL — über die Rückweg-Karte, statt
 * die Zeichen wie das Vorbild nachzuzählen (dessen Kommentar räumt den
 * Überschuss selbst ein). Fenster: die letzten 200 Zeichen vor der
 * Einfügestelle; kürzer als 12 Zeichen gilt nicht als Wiederholung, sonst
 * träfe es zufällige Satzanfänge.
 *
 * BEKANNTE GRENZE (Review Runde 2, benannt statt still): Satzzeichen zählen
 * mit. Endet der Nutzertext auf ``.`` und lässt das Modell den Punkt weg
 * (``"Wir liefern am Montag."`` → ``"Wir liefern am Montag die Ware aus."``),
 * greift die Bremse nicht — der Vergleichs-Schwanz endet auf ``.``, der
 * erzeugte Text an derselben Stelle auf ``" "``.
 *
 * Absichtlich NICHT „einfach die Satzzeichen wegtrimmen": dann schnitte die
 * Bremse zwar, und übrig bliebe ``"Wir liefern am Montag. die Ware aus."`` —
 * ein Satzfragment hinter einem geschlossenen Satz. Beide Ausgänge sind falsch,
 * nur unterschiedlich falsch; welcher der bessere ist, entscheidet man an
 * echten Modell-Antworten und nicht nebenbei. Bis dahin bleibt der Unterschlag
 * stehen, weil er wenigstens den ganzen Modellsatz erhält.
 */
export function stripLeadingRepeat(erzeugt: string, davor: string): BremsErgebnis {
  if (!erzeugt || !davor) return { text: erzeugt, entfernt: 0 };
  const schwanz = davor.slice(-FENSTER).trim();
  if (!schwanz) return { text: erzeugt, entfernt: 0 };

  const s = normalisiere(schwanz).text.trim();
  const g = normalisiere(erzeugt);
  if (!s || !g.text) return { text: erzeugt, entfernt: 0 };

  // Führenden Leerraum des Erzeugten überspringen (normalisiert höchstens eins).
  const start = g.text.startsWith(" ") ? 1 : 0;
  const max = Math.min(s.length, g.text.length - start);
  for (let len = max; len >= MIN_UEBERLAPPUNG; len--) {
    if (g.text.startsWith(s.slice(-len), start)) {
      const schnitt = g.karte[start + len];
      const rest = erzeugt.slice(schnitt).replace(/^[\s,.;:!?]+/, "");
      return { text: rest, entfernt: schnitt };
    }
  }
  return { text: erzeugt, entfernt: 0 };
}
